import { motion } from "framer-motion";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";

// --- Home Page Component ---
const HomePage = ({ setCurrentPage, theme }) => {
  // Receive theme prop
  const particlesInit = async (engine) => {
    await loadSlim(engine);
  };

  const particlesLoaded = async (container) => {
    console.log(container);
  };

  // Define particle options based on theme
  const particlesOptions = {
    background: {
      color: {
        value: "transparent",
      },
    },
    fpsLimit: 120,
    interactivity: {
      events: {
        onClick: {
          enable: true,
          mode: "push",
        },
        onHover: {
          enable: true,
          mode: "repulse",
        },
        resize: true,
      },
      modes: {
        push: {
          quantity: 4,
        },
        repulse: {
          distance: 100,
          duration: 0.4,
        },
      },
    },
    particles: {
      color: {
        value: theme === "dark" ? "#ffffff" : "#000000", // White particles in dark mode, black in light mode
      },
      links: {
        color: theme === "dark" ? "#ffffff" : "#000000", // White links in dark mode, black in light mode
        distance: 150,
        enable: true,
        opacity: 0.5,
        width: 1,
      },
      move: {
        direction: "none",
        enable: true,
        outModes: {
          default: "bounce",
        },
        random: false,
        speed: 2,
        straight: false,
      },
      number: {
        density: {
          enable: true,
          area: 800,
        },
        value: 80,
      },
      opacity: {
        value: 0.5,
      },
      shape: {
        type: "circle",
      },
      size: {
        value: { min: 1, max: 5 },
      },
    },
    detectRetina: true,
  };

  const gradientBackgroundClass =
    theme === "dark"
      ? "bg-gradient-to-br from-gray-900 to-gray-800" // Black and dark cream gradient for dark mode
      : "bg-gradient-to-br from-white to-amber-50"; // White and light cream gradient for light mode

  const textColorClass = theme === "dark" ? "text-white" : "text-gray-800";
  const gradientTextClass =
    theme === "dark"
      ? "from-purple-300 via-pink-300 to-red-300"
      : "from-gray-700 to-gray-900"; // Darker gradient for light mode text

  const buttonStyleClass =
    theme === "dark"
      ? "bg-translate text-purple-600 hover:bg-gray-100"
      : "bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700";

  // Variants for staggered text animation

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08, // Delay between each character
        delayChildren: 0.5, // Delay before the first character starts
      },
    },
  };

  const letterVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 800,
      },
    },
  };

  const greetingText = "Hello, I'm Ranjan.";
  const parts = greetingText.split(/(\s+)/); // Split by spaces, keeping spaces

  return (
    <section
      id="home"
      className={`relative h-screen flex items-center justify-center text-center overflow-hidden ${gradientBackgroundClass}`}
    >
      <Particles
        id="tsparticles"
        init={particlesInit}
        loaded={particlesLoaded}
        options={particlesOptions}
        className="absolute inset-0 z-0"
      />
      <div className="relative z-10 p-8 max-w-4xl mx-auto">
        <motion.h1
          className={`text-5xl md:text-7xl mb-4 p-5 font-semibold leading-tight bg-clip-text text-transparent bg-gradient-to-r ${gradientTextClass} animate-gradient-text`}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {parts.map((part, partIndex) =>
            part.trim() === "" ? (
              <span key={partIndex}>{part}</span> // Render spaces directly
            ) : (
              <span key={partIndex}>
                {part.split("").map((char, charIndex) => (
                  <motion.span key={charIndex} variants={letterVariants}>
                    {char}
                  </motion.span>
                ))}
              </span>
            )
          )}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          className={`text-xl md:text-2xl ${textColorClass} mb-8`}
        >
          A passionate Web Developer crafting beautiful and functional digital
          experiences.
        </motion.p>
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
          whileHover={{ scale: 1.05, boxShadow: "0 10px 20px rgba(0,0,0,0.2)" }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setCurrentPage("about")}
          className={`px-8 py-4 font-semibold rounded-full shadow-lg transition-all duration-200 text-lg ${buttonStyleClass}`}
        >
          Know More
        </motion.button>
      </div>
    </section>
  );
};

export default HomePage;